import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";
import {
  Share2,
  Copy,
  Link as LinkIcon,
  MessageCircle,
  Mail,
  QrCode,
  Smartphone,
  Download,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { usePWAInstall } from "@/contexts/PWAInstallContext";

/**
 * ShareAppButton — "share the app itself" entry point.
 *
 * Surfaces actions: Copy link, Native share, WhatsApp, Email, QR, Install PWA.
 * `variant` controls how it renders:
 *  - "icon"   → small icon-only button (Topbar)
 *  - "compact" → icon + label
 *  - "menu-item" → renders inline inside an existing DropdownMenu via children
 *  - "wide"   → full primary CTA (Help page, public footer)
 */
export default function ShareAppButton({
  variant = "icon",
  url,
  shareUrl,
  className = "",
  testId = "share-app-btn",
}) {
  const { t } = useTranslation();
  const { canInstall, install } = usePWAInstall();
  const [qrOpen, setQrOpen] = useState(false);

  const targetUrl = useMemo(() => {
    if (shareUrl) return shareUrl;
    if (url) return url;
    if (typeof window !== "undefined") return window.location.origin;
    return "";
  }, [url, shareUrl]);

  const shareTitle = t("share.app.title");
  const shareText = t("share.app.message");
  const subject = encodeURIComponent(t("share.app.mailSubject"));
  const body = encodeURIComponent(`${shareText}\n\n${targetUrl}`);
  const waText = encodeURIComponent(`${shareText} ${targetUrl}`);

  const canNativeShare =
    typeof navigator !== "undefined" && typeof navigator.share === "function";

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(targetUrl);
      toast.success(t("share.app.copied"));
    } catch {
      toast.error(t("share.app.copyError"));
    }
  };

  const onNativeShare = async () => {
    try {
      await navigator.share({
        title: shareTitle,
        text: shareText,
        url: targetUrl,
      });
    } catch {
      /* user cancelled — silent */
    }
  };

  const onDownloadQr = () => {
    const canvas = document.getElementById("share-app-qr-canvas");
    if (!canvas) return;
    const dataUrl = canvas.toDataURL("image/png");
    const a = document.createElement("a");
    a.href = dataUrl;
    a.download = "distribucion-canales-qr.png";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const onInstall = async () => {
    const accepted = await install();
    if (accepted) toast.success(t("share.app.installSuccess"));
  };

  const Trigger = (
    <Button
      variant={variant === "wide" ? "default" : "outline"}
      size={variant === "icon" ? "icon" : "sm"}
      className={`gap-2 ${className}`}
      data-testid={testId}
      title={t("share.app.title")}
    >
      <Share2 className="h-4 w-4" />
      {variant !== "icon" && (
        <span className="text-sm">{t("share.app.title")}</span>
      )}
    </Button>
  );

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>{Trigger}</DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className="min-w-56"
          data-testid="share-app-menu"
        >
          <DropdownMenuLabel className="text-xs uppercase tracking-wider text-muted-foreground">
            {t("share.app.menuTitle")}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {canNativeShare && (
            <DropdownMenuItem
              data-testid="share-app-native"
              onClick={onNativeShare}
            >
              <Smartphone className="mr-2 h-4 w-4" />
              {t("share.app.native")}
            </DropdownMenuItem>
          )}
          <DropdownMenuItem data-testid="share-app-copy" onClick={onCopy}>
            <LinkIcon className="mr-2 h-4 w-4" />
            {t("share.app.copyLink")}
          </DropdownMenuItem>
          <DropdownMenuItem
            data-testid="share-app-whatsapp"
            onClick={() =>
              window.open(
                `https://wa.me/?text=${waText}`,
                "_blank",
                "noopener,noreferrer"
              )
            }
          >
            <MessageCircle className="mr-2 h-4 w-4" />
            {t("share.app.whatsapp")}
          </DropdownMenuItem>
          <DropdownMenuItem
            data-testid="share-app-email"
            onClick={() => {
              window.location.href = `mailto:?subject=${subject}&body=${body}`;
            }}
          >
            <Mail className="mr-2 h-4 w-4" />
            {t("share.app.email")}
          </DropdownMenuItem>
          <DropdownMenuItem
            data-testid="share-app-qr"
            onClick={() => setQrOpen(true)}
          >
            <QrCode className="mr-2 h-4 w-4" />
            {t("share.app.qr")}
          </DropdownMenuItem>
          {canInstall && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                data-testid="share-app-install"
                onClick={onInstall}
              >
                <Copy className="mr-2 h-4 w-4 rotate-90" />
                {t("share.app.install")}
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={qrOpen} onOpenChange={setQrOpen}>
        <DialogContent
          className="max-w-sm"
          data-testid="share-app-qr-dialog"
        >
          <DialogHeader>
            <DialogTitle>{t("share.app.qrTitle")}</DialogTitle>
            <DialogDescription>
              {t("share.app.qrDescription")}
            </DialogDescription>
          </DialogHeader>
          <div
            className="flex flex-col items-center gap-3 rounded-lg bg-white p-5"
            data-testid="share-app-qr-frame"
          >
            <QRCodeCanvas
              id="share-app-qr-canvas"
              value={targetUrl}
              size={224}
              includeMargin
              level="M"
            />
            <code className="break-all rounded bg-slate-100 px-2 py-1 text-[11px] text-slate-700">
              {targetUrl}
            </code>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={onDownloadQr}
              data-testid="share-app-qr-download"
            >
              <Download className="mr-2 h-4 w-4" />
              {t("share.qr.download")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
